import { Auth } from 'aws-amplify';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import i18next from 'i18next';
import authentication from 'api/authentication';
import people from 'api/people';

import {
  COMPLETE_NEW_PASSWORD_SUBMIT,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_RESEND_OTP,
  FORGOT_PASSWORD_SUBMIT,
  GET_CURRENT_USER,
  GET_CURRENT_USER_ACCOUNT_INFO,
  SIGNIN,
  SIGNOUT,
  UPDATE_USER_PROFILE_DATA
} from 'redux/sagas/actions/authentication';
import { LOADER_CLOSE, LOADER_OPEN } from 'redux/slices/app';
import {
  FORCE_PASSWORD_CHANGE,
  RESEND_OTP_IS_LOADING,
  RESEND_OTP_IS_NOT_LOADING
} from 'redux/slices/authentication';
import { MODAL_CLOSE } from 'redux/slices/modals';

import { Toast } from 'ui/components';
import { cognitoErrors } from '../../const';

function* getCurrentUserAccountInfo(username) {
  try {
    const { data } = yield call(
      authentication.getCurrentUserAccountInfo,
      username
    );
    yield put({ type: GET_CURRENT_USER_ACCOUNT_INFO._SUCCESS, payload: data });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: GET_CURRENT_USER_ACCOUNT_INFO._ERROR,
      payload: err.message
    });
  }
}

function* getCurrentUserAccountInfoWatch() {
  yield takeLatest(
    GET_CURRENT_USER_ACCOUNT_INFO._REQUEST,
    getCurrentUserAccountInfo
  );
}

function* signIn({ payload }) {
  try {
    yield put(LOADER_OPEN());
    const { user: userName, password } = payload;
    const response = yield call([Auth, Auth.signIn], userName, password);

    const { challengeName } = response;

    if (challengeName && challengeName === 'NEW_PASSWORD_REQUIRED') {
      yield put({
        type: FORCE_PASSWORD_CHANGE,
        payload: {
          user: response,
          requiredAttributes: response?.challengeParam?.requiredAttributes
        }
      });
    } else {
      const {
        username,
        signInUserSession: { idToken, refreshToken, accessToken },
        attributes
      } = response;

      yield call(getCurrentUserAccountInfo, username);

      yield put({
        type: SIGNIN._SUCCESS,
        payload: {
          username,
          idToken: idToken.jwtToken,
          refreshToken: refreshToken.token,
          accessToken: accessToken.jwtToken,
          attributes
        }
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > login > signin', err);

    let message = err.message || 'Error on SIGNIN';

    if (message === 'User does not exist.') {
      message = 'Invalid credentials';
    }

    yield put({
      type: SIGNIN._ERROR,
      payload: message
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* signinWatch() {
  yield takeLatest(SIGNIN._REQUEST, signIn);
}

function* forgotPassword({ payload }) {
  try {
    yield put(LOADER_OPEN());
    // eslint-disable-next-line no-unused-vars
    const res = yield call([Auth, Auth.forgotPassword], payload);

    yield put({
      type: FORGOT_PASSWORD._SUCCESS,
      payload
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > signin > FORGOT_PASSWORD', err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: FORGOT_PASSWORD._ERROR,
      payload: err.message || 'Error on FORGOT_PASSWORD'
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* forgotPasswordWatch() {
  yield takeLatest(FORGOT_PASSWORD._REQUEST, forgotPassword);
}

function* forgotPasswordResendOtp() {
  try {
    yield put(RESEND_OTP_IS_LOADING());
    // eslint-disable-next-line no-unused-vars
    const userNameEmail = yield select(
      state => state.authentication?.accountInfo?.email
    );
    const userNameForgotData = yield select(
      state => state.authentication?.forgotData?.userName
    );
    yield call(
      [Auth, Auth.forgotPassword],
      userNameEmail || userNameForgotData
    );

    yield put({
      type: FORGOT_PASSWORD_RESEND_OTP._SUCCESS,
      payload: userNameEmail || userNameForgotData
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > signin > FORGOT_PASSWORD', { err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    const errorMessage = cognitoErrors[err?.code];
    yield put({
      type: FORGOT_PASSWORD_RESEND_OTP._ERROR,
      payload: i18next.t(errorMessage) || i18next.t('generic_otp_error')
    });
  } finally {
    yield put(RESEND_OTP_IS_NOT_LOADING());
  }
}

function* forgotPasswordResendOtpWatch() {
  yield takeLatest(
    FORGOT_PASSWORD_RESEND_OTP._REQUEST,
    forgotPasswordResendOtp
  );
}

function* forgotPasswordSubmit({ payload }) {
  try {
    yield put(LOADER_OPEN());
    const {
      forgotData: { code, userName }
    } = yield select(state => state.authentication);
    // eslint-disable-next-line no-unused-vars
    const res = yield call(
      [Auth, Auth.forgotPasswordSubmit],
      userName,
      code,
      payload
    );

    yield put({
      type: FORGOT_PASSWORD_SUBMIT._SUCCESS
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > chargers > FORGOT_PASSWORD_SUBMIT', err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: FORGOT_PASSWORD_SUBMIT._ERROR,
      payload: cognitoErrors[err?.code] || 'Error on FORGOT_PASSWORD_SUBMIT'
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* forgotPasswordSubmitWatch() {
  yield takeLatest(FORGOT_PASSWORD_SUBMIT._REQUEST, forgotPasswordSubmit);
}

function* getCurrentUser() {
  try {
    yield put(LOADER_OPEN());
    const {
      username,
      signInUserSession: { idToken, refreshToken, accessToken },
      attributes
    } = yield call([Auth, Auth.currentAuthenticatedUser]);

    yield call(getCurrentUserAccountInfo, username);

    yield put({
      type: GET_CURRENT_USER._SUCCESS,
      payload: {
        username,
        idToken: idToken.jwtToken,
        refreshToken: refreshToken.token,
        accessToken: accessToken.jwtToken,
        attributes
      }
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: GET_CURRENT_USER._ERROR,
      payload: err.message || 'Error on GET_CURRENT_USER'
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* getCurrentUserWatch() {
  yield takeLatest(GET_CURRENT_USER._REQUEST, getCurrentUser);
}

function* signOut() {
  try {
    yield put(LOADER_OPEN());
    // eslint-disable-next-line no-unused-vars
    const res = yield call([Auth, Auth.signOut]);
    yield put({
      type: SIGNOUT._SUCCESS
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > chargers > SIGNOUT', err);

    yield put({
      type: SIGNOUT._ERROR,
      payload:
        err.message ||
        'System is unable to process the request, please try again'
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* signOutWatch() {
  yield takeLatest(SIGNOUT._REQUEST, signOut);
}

function* completeNewPassword({ payload }) {
  try {
    yield put(LOADER_OPEN());
    const {
      completeNewPasswordData: { user, requiredAttributes }
    } = yield select(state => state.authentication);

    const {
      username,
      signInUserSession: { idToken, refreshToken, accessToken },
      attributes
    } = yield call(
      [Auth, Auth.completeNewPassword],
      user,
      payload,
      ...requiredAttributes
    );

    yield call(getCurrentUserAccountInfo, username);

    yield call(people.profileEnable);

    yield put({
      type: COMPLETE_NEW_PASSWORD_SUBMIT._SUCCESS,
      payload: {
        username,
        idToken: idToken.jwtToken,
        refreshToken: refreshToken.token,
        accessToken: accessToken.jwtToken,
        attributes
      }
    });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: COMPLETE_NEW_PASSWORD_SUBMIT._ERROR,
      payload: err.message || 'Error on COMPLETE_NEW_PASSWORD_SUBMIT'
    });
  } finally {
    yield put(LOADER_CLOSE());
  }
}

function* completeNewPasswordWatch() {
  yield takeLatest(COMPLETE_NEW_PASSWORD_SUBMIT._REQUEST, completeNewPassword);
}
function* updateUserProfileData({ payload }) {
  try {
    const username = yield select(state => state.authentication.user.username);
    const { state, isOnLoginFlow } = payload;
    yield put(LOADER_OPEN());
    yield call(authentication.updateCurrentUserAccountInfo, {
      name: state.name,
      surname: state.lastName,
      phoneNumberComplete: `${
        state.prefix.value ? state.prefix.value : state.prefix
      }${state.phoneNumber}`,
      phoneNumber: state.phoneNumber,
      phoneNumberPrefix: state.prefix.value
    });
    yield put({
      type: UPDATE_USER_PROFILE_DATA._SUCCESS,
      payload: {
        phoneNumber: `${
          state.prefix.value ? state.prefix.value : state.prefix
        }${state.phoneNumber}`,
        phoneNumberPrefix: state.prefix.value,
        phoneNumberPart: state.phoneNumber,
        name: state.name,
        lastName: state.lastName
      }
    });

    if (!isOnLoginFlow) {
      yield put(MODAL_CLOSE());
      Toast.show(
        Toast.TYPE.SUCCESS,
        i18next.t('change_personal_data_toast_success_message')
      );
    }

    yield put({
      type: GET_CURRENT_USER_ACCOUNT_INFO._REQUEST,
      payload: { username }
    });

    yield put(LOADER_CLOSE());
  } catch (err) {
    console.log(err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put(LOADER_CLOSE());
    yield put({ type: UPDATE_USER_PROFILE_DATA._ERROR });
    yield put(MODAL_CLOSE());
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('change_personal_data_toast_error_message')
    );
  }
}
function* updateUserProfileDataWatcher() {
  yield takeLatest(UPDATE_USER_PROFILE_DATA._REQUEST, updateUserProfileData);
}

export default [
  signinWatch(),
  forgotPasswordWatch(),
  forgotPasswordResendOtpWatch(),
  forgotPasswordSubmitWatch(),
  getCurrentUserWatch(),
  signOutWatch(),
  getCurrentUserAccountInfoWatch(),
  completeNewPasswordWatch(),
  updateUserProfileDataWatcher()
];
